import { useState, useEffect } from 'react';
import { useTheme } from '../../../context/theme';
import { useToast } from '../../../context/toast';
import { api } from '../../../utils/api';
import { renderIcon } from '../../../utils/icons';
import { Btn, Inp, Card, EmptyState } from '../../../components/ui';
import { SkeletonList } from '../../../components/ui/Skeletons';

export default function AccountsPayableTab({ siteId, site, user }) {
  const { theme, isDark } = useTheme();
  const { toast } = useToast();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [aged, setAged] = useState(null);
  const [showAged, setShowAged] = useState(false);
  const [form, setForm] = useState({ po_id: '', invoice_number: '', invoice_date: '', amount: '', description: '', entity_id: '' });
  const [entities, setEntities] = useState([]);
  const [file, setFile] = useState(null);
  const isAdmin = user.role === 'admin';
  const isViewer = user.role === 'viewer';

  const load = async () => {
    try {
      const [b, p, ent] = await Promise.all([
        api.get('/api/sites/' + siteId + '/bills'),
        api.get('/api/sites/' + siteId + '/pos'),
        api.get('/api/entities'),
      ]);
      setBills(b.bills || []);
      setPos((p.purchaseOrders || []).filter(po => ['Approved', 'Issued', 'Partially Billed'].includes(po.status)));
      setEntities(ent.entities || []);
      setForm(f => ({ ...f, entity_id: site?.entity_id ? String(site.entity_id) : '' }));
      setLoading(false);
    } catch (e) { setLoading(false); }
  };

  useEffect(() => { load(); }, [siteId]);

  const loadAged = async () => {
    try { const a = await api.get('/api/sites/' + siteId + '/bills/aged'); setAged(a); setShowAged(!showAged); }
    catch (e) { toast(e.message, 'error'); }
  };

  const submitBill = async () => {
    try {
      if (!form.po_id || !form.amount || !file) return toast('PO, amount, and invoice PDF required', 'error');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('po_id', form.po_id);
      fd.append('invoice_number', form.invoice_number);
      fd.append('invoice_date', form.invoice_date);
      fd.append('amount', form.amount);
      fd.append('description', form.description);
      if (form.entity_id) fd.append('entity_id', form.entity_id);
      const r = await fetch('/api/sites/' + siteId + '/bills', { method: 'POST', headers: { Authorization: 'Bearer ' + api.token }, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      toast('Bill submitted');
      setShowForm(false);
      setForm({ po_id: '', invoice_number: '', invoice_date: '', amount: '', description: '', entity_id: site?.entity_id ? String(site.entity_id) : '' });
      setFile(null);
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const advanceBill = async (id) => {
    try { await api.put('/api/bills/' + id + '/approve', { action: 'advance' }); toast('Bill advanced'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const payBill = async (id) => {
    const check = prompt('Check/Wire # (optional):');
    try { await api.put('/api/bills/' + id + '/payment', { check_wire_number: check || '', payment_method: check ? 'Check' : 'Wire' }); toast('Payment recorded'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const deleteBill = async (id, billNumber) => {
    if (!confirm('Delete ' + billNumber + '? This cannot be undone.')) return;
    try { await api.del('/api/bills/' + id); toast('Bill deleted'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const statusBadge = (s) => {
    const colors = { 'Submitted': ['#fef3c7', '#F59E0B'], 'PM Review': ['#dbeafe', '#2563eb'], 'Finance Approval': ['#ede9fe', '#7c3aed'], 'Approved for Payment': ['#dcfce7', '#4ADE80'], 'Paid': ['#f0fdf4', '#4ADE80'] };
    const c = colors[s] || ['rgba(255,255,255,0.03)', 'rgba(255,255,255,0.58)'];
    return <span style={{ background: c[0], color: c[1], padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{s}</span>;
  };

  if (loading) return <Card><SkeletonList count={4} /></Card>;

  const selStyle = { padding: '8px 12px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 8, color: theme.text, fontSize: 13 };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>
          Accounts Payable<span style={{ color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>({bills.length})</span>
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <a href={'/accounting?site_id=' + siteId} onClick={e => { e.preventDefault(); window.history.pushState({ view: 'accounting' }, '', '/accounting?site_id=' + siteId); window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'accounting' } })); }} style={{ fontSize: 12, color: theme.accent, textDecoration: 'none' }}>View in Accounting →</a>
          <Btn sm onClick={loadAged} style={{ background: 'transparent', border: '1px solid ' + theme.border }}>{showAged ? 'Hide Aged' : 'Aged Payables'}</Btn>
          {!isViewer && <Btn sm primary onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : '+ Submit Bill'}</Btn>}
          <a href={'/api/sites/' + siteId + '/bills/export'} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid ' + theme.border, borderRadius: 8, color: theme.textSecondary, fontSize: 12, textDecoration: 'none' }}>{renderIcon('download', 12)} CSV</a>
        </div>
      </div>

      {/* Aged Payables */}
      {showAged && aged && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
          {['current', 'days30', 'days60', 'days90'].map((k, i) => (
            <div key={k} style={{ padding: 12, background: i === 3 && aged.totals[k] > 0 ? '#fecaca30' : isDark ? '#1e1e24' : '#f8f9fa', border: '1px solid ' + theme.border, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>{['Current', '30 Days', '60 Days', '90+ Days'][i]}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: i === 3 && aged.totals[k] > 0 ? '#EF4444' : theme.text, marginTop: 4 }}>${Number(aged.totals[k] || 0).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: theme.textSecondary }}>{aged.buckets[k]?.length || 0} bills</div>
            </div>
          ))}
        </div>
      )}

      {/* Submit Bill Form */}
      {showForm && (
        <div style={{ background: isDark ? '#1e1e24' : '#f8f9fa', border: '1px solid ' + theme.border, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          {pos.length === 0
            ? <p style={{ color: '#EF4444', fontSize: 13 }}>No open POs to bill against. Create and approve a PO first.</p>
            : <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <select value={form.po_id} onChange={e => setForm({ ...form, po_id: e.target.value })} style={selStyle}>
                  <option value="">Select PO *</option>
                  {pos.map(p => <option key={p.id} value={p.id}>{p.po_number} — {p.vendor_name} (${Number(p.amount).toLocaleString()})</option>)}
                </select>
                <Inp type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="Amount ($) *" />
                <Inp value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} placeholder="Vendor's Invoice #" />
                <Inp type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} />
                <select value={form.entity_id} onChange={e => setForm({ ...form, entity_id: e.target.value })} style={selStyle}>
                  <option value="">Entity (auto from site)</option>
                  {entities.map(en => <option key={en.id} value={en.id}>{en.short_name || en.name}</option>)}
                </select>
              </div>
              <Inp value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" style={{ marginTop: 10, width: '100%' }} />
              <div style={{ marginTop: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: theme.bgCard, border: '2px dashed ' + (file ? '#4ADE80' : theme.border), borderRadius: 8, cursor: 'pointer', fontSize: 13, color: file ? '#4ADE80' : theme.textSecondary }}>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                  {renderIcon('upload', 16)}{file ? '✅ ' + file.name : 'Upload Invoice PDF *'}
                </label>
              </div>
              <Btn sm primary onClick={submitBill} style={{ marginTop: 10 }}>Submit Bill</Btn>
            </>
          }
        </div>
      )}

      {bills.length === 0 && !showForm && <EmptyState icon="file-text" title="No bills submitted" description="Submit a bill against an approved PO to track payables" action={!isViewer ? '+ Submit Bill' : undefined} onAction={!isViewer ? () => setShowForm(true) : undefined} />}

      {bills.length > 0 && (
        <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid ' + theme.border }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 100px 2fr 100px 1fr 1fr 1fr 80px', padding: '8px 12px', background: isDark ? '#2a2a32' : '#f1f5f9', fontSize: 11, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', gap: 8 }}>
            <span>Bill #</span><span>PO #</span><span>Vendor</span><span>Invoice #</span><span style={{ textAlign: 'right' }}>Amount</span><span>Status</span><span>Paid</span><span></span>
          </div>
          {bills.map((b, i) => (
            <div key={b.id} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '100px 100px 2fr 100px 1fr 1fr 1fr 80px', padding: '8px 12px', borderTop: '1px solid ' + theme.border, fontSize: 12, alignItems: 'center', gap: 8, cursor: 'default', background: i % 2 === 0 ? 'transparent' : (isDark ? '#ffffff05' : 'rgba(255,255,255,0.03)') }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#4ADE80' }}>{b.bill_number}</span>
              <span style={{ fontFamily: 'monospace', color: theme.textSecondary }}>{b.po_number}</span>
              <span style={{ color: theme.text }}>{b.vendor_name}</span>
              <span style={{ color: theme.textSecondary }}>{b.invoice_number || '—'}</span>
              <span style={{ textAlign: 'right', fontWeight: 600, color: theme.text }}>${Number(b.amount).toLocaleString()}</span>
              {statusBadge(b.status)}
              <span style={{ fontSize: 11, color: b.paid_date ? '#4ADE80' : theme.textSecondary }}>{b.paid_date ? new Date(b.paid_date).toLocaleDateString() : '—'}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {b.invoice_blob_url && <a href={b.invoice_blob_url} target="_blank" style={{ color: '#4ADE80', fontSize: 11 }}>PDF</a>}
                {isAdmin && b.status !== 'Paid' && b.status !== 'Approved for Payment' && <button onClick={() => advanceBill(b.id)} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer' }}>Advance</button>}
                {isAdmin && b.status === 'Approved for Payment' && <button onClick={() => payBill(b.id)} style={{ background: '#4ADE80', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer' }}>Pay</button>}
                {isAdmin && b.status !== 'Paid' && <button onClick={() => deleteBill(b.id, b.bill_number)} title="Delete bill" style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: 12, cursor: 'pointer', padding: '2px 4px', opacity: 0.7 }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}>✕</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
